/**
 * ConnectionEditDialogShell — shared chrome for the manifest-driven
 * ConnectionList edit dialogs (managed / ARM + service-provider).
 *
 * Both dialogs render an identical Fluent Dialog scaffold: the surface, the
 * title with a Close affordance, a scrollable content area, a Cancel / Save
 * footer, and the sibling DiscardChangesDialog — all wired to a shared
 * `useDiscardGuard`. This component owns that chrome so no connection-specific
 * logic lives here: each dialog supplies its own body via `children` and its
 * own save orchestration via `onSave` + the `isBusy` / `canSave` flags.
 *
 * The Cancel/Save disabled semantics are preserved from both call sites:
 *   - Cancel is disabled while a save is in flight (`isBusy`).
 *   - Save is disabled unless the form is dirty (`canSave`) and idle (`!isBusy`).
 */

import type { ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  Spinner,
  tokens,
} from '@coreai/fluentui-react';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { DiscardChangesDialog } from './DiscardChangesDialog';
import type { DiscardGuard } from './useDiscardGuard';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '800px',
    minWidth: '700px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  footer: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

export interface ConnectionEditDialogShellProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Dialog title text. */
  title: string;
  /** Shared close/discard orchestration (see `useDiscardGuard`). */
  discardGuard: DiscardGuard;
  /** Save/mutation in flight — disables Cancel and shows the Save spinner. */
  isBusy: boolean;
  /** Whether Save is enabled (typically the form's dirty flag). */
  canSave: boolean;
  /** Save handler for the primary button. */
  onSave: () => void;
  /** Primary button label. Defaults to Save/Saving… wording. */
  primaryLabel?: string;
  /** Primary button label while `isBusy`. */
  primaryBusyLabel?: string;
  /**
   * Extra class merged onto the DialogSurface — used to preserve each dialog's
   * own `minHeight` (managed 500px vs service-provider 480px).
   */
  surfaceClassName?: string;
  /** Dialog body (form + any dialog-specific sections). */
  children: ReactNode;
}

export const ConnectionEditDialogShell = ({
  open,
  title,
  discardGuard,
  isBusy,
  canSave,
  onSave,
  primaryLabel = 'Save',
  primaryBusyLabel = 'Saving…',
  surfaceClassName,
  children,
}: ConnectionEditDialogShellProps) => {
  const styles = useStyles();

  return (
    <>
      <Dialog open={open} onOpenChange={discardGuard.handleOpenChange}>
        <DialogSurface className={mergeClasses(styles.dialogSurface, surfaceClassName)}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={discardGuard.requestClose}
                />
              }
            >
              {title}
            </DialogTitle>
            <DialogContent className={styles.content}>{children}</DialogContent>
            <DialogActions className={styles.footer}>
              <Button
                appearance="secondary"
                onClick={discardGuard.requestClose}
                disabled={isBusy}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={onSave}
                disabled={!canSave || isBusy}
                {...(isBusy ? { icon: <Spinner size="tiny" /> } : {})}
              >
                {isBusy ? primaryBusyLabel : primaryLabel}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Discard-confirmation dialog — shared across every ConnectionList edit
          surface for consistent copy + affordances. Rendered as a sibling of
          the main dialog so its open state is independent. */}
      <DiscardChangesDialog
        open={discardGuard.showDiscardConfirm}
        onKeepEditing={discardGuard.keepEditing}
        onDiscard={discardGuard.confirmDiscard}
      />
    </>
  );
};

ConnectionEditDialogShell.displayName = 'ConnectionEditDialogShell';
